import {
  HomeIcon,
  MicrophoneIcon,
  DocumentTextIcon,
  QueueListIcon,
  PuzzlePieceIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  MicrophoneIcon as MicrophoneIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  QueueListIcon as QueueListIconSolid,
  PuzzlePieceIcon as PuzzlePieceIconSolid,
  CreditCardIcon as CreditCardIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
} from '@heroicons/react/24/solid';

// Sidebar navigation items
export const NAV_ITEMS = [
  {
    label: 'Dashboard',
    to: '/app/dashboard',
    Icon: HomeIcon,
    IconActive: HomeIconSolid,
  },
  {
    label: 'Audio',
    to: '/app/audio',
    Icon: MicrophoneIcon,
    IconActive: MicrophoneIconSolid,
  },
  {
    label: 'Documents',
    to: '/app/documents',
    Icon: DocumentTextIcon,
    IconActive: DocumentTextIconSolid,
  },
  {
    label: 'Tickets',
    to: '/app/tickets',
    Icon: QueueListIcon,
    IconActive: QueueListIconSolid,
  },
  {
    label: 'Integrations',
    to: '/app/integrations',
    Icon: PuzzlePieceIcon,
    IconActive: PuzzlePieceIconSolid,
  },
  {
    label: 'Billing',
    to: '/app/billing',
    Icon: CreditCardIcon,
    IconActive: CreditCardIconSolid,
  },
  {
    label: 'Settings',
    to: '/app/settings',
    Icon: Cog6ToothIcon,
    IconActive: Cog6ToothIconSolid,
  },
];

// Route → page title mapping for the app shell header
export const PAGE_TITLES = {
  '/app/dashboard': 'Dashboard',
  '/app/audio': 'Audio Capture',
  '/app/documents': 'Documents',
  '/app/tickets': 'Tickets',
  '/app/integrations': 'Integrations',
  '/app/billing': 'Billing',
  '/app/settings': 'Settings',
};

// App route paths
export const ROUTES = {
  LANDING: '/',
  DASHBOARD: '/app/dashboard',
  AUDIO: '/app/audio',
  DOCUMENTS: '/app/documents',
  TICKETS: '/app/tickets',
  INTEGRATIONS: '/app/integrations',
  BILLING: '/app/billing',
  SETTINGS: '/app/settings',
  LOGIN: '/login',
  SIGNUP: '/signup',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  THANK_YOU: '/thank-you',
};

// Landing page section anchor IDs
export const LANDING_SECTION_IDS = {
  HERO: 'hero',
  FEATURES: 'features',
  USE_CASES: 'use-cases',
  INTEGRATIONS: 'integrations',
  PRICING: 'pricing',
  FAQ: 'faq',
  JOIN_US: 'join-us',
};

// Landing page header nav items
export const LANDING_NAV_ITEMS = [
  { label: 'Features', sectionId: 'features' },
  { label: 'Use Cases', sectionId: 'use-cases' },
  { label: 'Integrations', sectionId: 'integrations' },
  { label: 'Pricing', sectionId: 'pricing' },
  { label: 'FAQ', sectionId: 'faq' },
];

// Feature showcases — top 3 alternating spotlight rows
export const FEATURE_SHOWCASES = [
  {
    id: 1,
    label: 'Voice Capture',
    title: 'Voice to Ticket',
    description:
      'Record yourself talking through a bug, feature request, or task — the app captures every detail exactly as you describe it.',
    badge: 'No audio stored',
    visualType: 'dark-panel',
    visualContent: {
      lines: [
        { type: 'comment', text: '// Recording in progress...' },
        { type: 'blank' },
        { type: 'string', text: '"The login redirect fails on mobile Safari' },
        { type: 'string', text: ' when the user has 2FA enabled. It loops' },
        { type: 'string', text: ' back to the login screen instead of the' },
        { type: 'string', text: ' dashboard..."' },
        { type: 'blank' },
        { type: 'success', text: '● Recording — 00:47' },
      ],
    },
  },
  {
    id: 2,
    label: 'Document Upload',
    title: 'Doc to Ticket',
    description:
      'Upload PRDs, meeting notes, or bug reports — the AI identifies and extracts individual tickets from the content automatically.',
    badge: 'No files stored',
    visualType: 'browser-mockup',
    visualContent: {
      fields: [
        { label: 'Source', value: 'sprint-planning-notes.pdf' },
        { label: 'Status', value: '✓ Parsed successfully' },
        { label: '', value: '' },
        { label: 'Extracted', value: '4 tickets found' },
        { label: '  #1', value: 'Refactor auth module for SSO support' },
        { label: '  #2', value: 'Add rate limiting to public API' },
        { label: '  #3', value: 'Fix mobile Safari redirect loop' },
        { label: '  #4', value: 'Update onboarding flow copy' },
      ],
    },
  },
  {
    id: 3,
    label: 'AI-Powered Structuring',
    title: 'Smart Structuring',
    description:
      'The AI turns your raw input into a fully structured ticket — clear title, detailed description, steps to reproduce, test cases, and acceptance criteria.',
    visualType: 'browser-mockup',
    visualContent: {
      fields: [
        { label: 'Title', value: 'Fix mobile Safari redirect loop with 2FA' },
        {
          label: 'Description',
          value: 'Users with 2FA enabled on mobile Safari are redirected back to login...',
        },
        {
          label: 'Steps to Reproduce',
          value: '1. Enable 2FA  2. Open Safari on iOS  3. Attempt login...',
        },
        {
          label: 'Test Cases',
          value: '• Verify redirect succeeds with 2FA on Safari, Chrome, Firefox',
        },
        {
          label: 'Acceptance Criteria',
          value: '• User lands on dashboard after 2FA verification on all browsers',
        },
      ],
    },
  },
];

// Feature cards — bottom 3 in a card grid
export const FEATURE_CARDS = [
  {
    id: 4,
    title: 'Edit & Approve',
    description:
      'Review, edit, and refine every generated ticket before it reaches your project management tool — nothing gets pushed without your approval.',
  },
  {
    id: 5,
    title: 'Zero Detail Loss',
    description:
      'The AI preserves technical context, environment details, and edge cases from your original input so tickets arrive complete and actionable.',
  },
  {
    id: 6,
    title: 'Instant Sync',
    description:
      'Connect with Jira today, with Linear, Asana, ClickUp, and Trello coming soon — your tickets land exactly where your team works.',
  },
];

// Use case cards — asymmetric grid (2 + 1 wide)
export const USE_CASE_CARDS = [
  {
    id: 1,
    title: 'The Meeting After the Meeting',
    description:
      'Your team just spent an hour breaking down the next sprint. Now someone has to sit down and manually write up every ticket from memory. Half the context is already gone.',
    wide: false,
    mockupContent: [
      { badge: 'P1', badgeColor: 'red', text: 'Fix checkout timeout on high-latency connections' },
      { badge: 'P2', badgeColor: 'yellow', text: 'Migrate user preferences to new settings schema' },
      { badge: 'P1', badgeColor: 'red', text: 'Add retry logic for failed webhook deliveries' },
    ],
  },
  {
    id: 2,
    title: 'The PRD Bottleneck',
    description:
      'Engineering is ready to start, but the PM is still on hour four of manually splitting one spec into twenty-something tickets. Everyone waits.',
    wide: false,
    mockupContent: [
      { label: 'Source', value: 'payments-redesign-rfc.md' },
      { label: 'Status', value: '✓ 7 tickets ready for review' },
      { divider: true },
      { label: '#1', value: 'Create Stripe webhook handler for refunds' },
      { label: '#2', value: 'Build transaction history filtering UI' },
    ],
  },
  {
    id: 3,
    title: 'Lost in Translation',
    description:
      "A customer hits a critical bug. Support writes it up, PM rephrases it, and by the time it reaches engineering the steps to reproduce are gone and the environment details are 'I think they said Chrome.' Three handoffs later, half the details are gone.",
    wide: true,
    mockupContent: [
      { check: true, text: 'Reproduce payment failure on Firefox + Linux', status: 'Ticket filed' },
      { check: true, text: 'Capture full stack trace from Sentry alert #4821', status: 'Ticket filed' },
      { check: true, text: 'Document API timeout conditions for /v2/orders', status: 'Ticket filed' },
    ],
  },
];

// Pricing tiers — coming soon / teaser style
export const PRICING_TIERS = [
  {
    id: 'free',
    name: 'Free',
    subtitle: 'No credit card. No commitment.',
    price: '$0',
    priceUnit: '/month',
    ctaLabel: 'Join Waitlist',
    ctaVariant: 'filled',
    comingSoon: false,
    features: [
      '10 credits per month',
      '1 project management tool',
      'Audio transcription',
      'AI ticket extraction',
      'Basic support',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    subtitle: 'For teams that ship fast',
    price: null,
    priceUnit: '/month',
    ctaLabel: 'Join Waitlist',
    ctaVariant: 'filled',
    comingSoon: true,
    features: [
      '50 credits per month',
      'Unlimited project management tools',
      'Priority processing',
      'Document upload',
      'Batch ticket creation',
      'Priority support',
    ],
  },
];

// Integrations section
export const INTEGRATIONS_SECTION = {
  heading: 'Integrations',
  subheading: 'Connect once, push anytime. Your tickets show up exactly where your team needs them.',
  items: [
    { id: 'jira', name: 'Jira', available: true },
    { id: 'asana', name: 'Asana', available: false },
    { id: 'trello', name: 'Trello', available: false },
    { id: 'clickup', name: 'ClickUp', available: false },
  ],
};

// CTA banner content (below pricing)
export const CTA_BANNER = {
  headline: 'Ready to Stop Losing Action Items?',
  subtitle: 'Be the first to turn conversations into tickets when we launch.',
};

// Join Us section content
export const JOIN_US_SECTION = {
  decorativeText: "We've reserved a spot for you",
  heading: 'Get Early Access',
  description: 'Join the waitlist and be among the first to turn your meetings into structured tickets — automatically.',
};

// Thank-you page content (shown after waitlist signup)
export const THANK_YOU_PAGE = {
  heading: "You're on the list!",
  subheading: "We'll send you an email as soon as Talk to Ticket is ready to use. Stay tuned.",
  ctaLabel: 'Back to Home',
  shareText: 'Just joined the waitlist for Talk to Ticket — turn meetings into structured Jira tickets with AI! Check it out:',
};

// Footer navigation items
export const FOOTER_NAV_ITEMS = [
  { label: 'Features', sectionId: 'features' },
  { label: 'Use Cases', sectionId: 'use-cases' },
  { label: 'Integrations', sectionId: 'integrations' },
  { label: 'Pricing', sectionId: 'pricing' },
  { label: 'FAQ', sectionId: 'faq' },
];

// FAQ items for landing page
export const FAQ_ITEMS = [
  {
    id: 'data-privacy',
    question: 'Is my audio or document data stored after processing?',
    answer:
      'No. Audio files and documents are deleted immediately after processing \u2014 we never store your original files. Transcriptions and extracted ticket data exist only in your session until you push them to Jira or discard them. Your Jira credentials are encrypted at rest using industry-standard encryption.',
  },
  {
    id: 'credit-card',
    question: 'Do I need a credit card to get started?',
    answer:
      'Not at all. The Free plan gives you credits every month \u2014 no credit card, no strings attached. Each audio recording uses just one credit. If you need more, you can upgrade to the Pro plan.',
  },
  {
    id: 'formats',
    question: 'What audio formats and file types are supported?',
    answer:
      'For audio, we support MP3, WAV, OGG, M4A, FLAC, WebM, and MP4 \u2014 currently in English. For documents, we accept PDF, Word, and Markdown files.',
  },
  {
    id: 'multiple-tickets',
    question: 'Can I get multiple tickets from a single upload?',
    answer:
      'Absolutely. When you upload a document like a PRD or meeting notes, the AI extracts up to 10 individual tickets from the content. For audio uploads, the AI also suggests 3\u20135 related child tickets for Epics and Stories to help you break work down further.',
  },
  {
    id: 'credits-run-out',
    question: 'What happens when I run out of credits?',
    answer:
      'You won\u2019t be able to process new uploads until your credits reset at the start of your next billing cycle. You can upgrade to Pro anytime to get more credits. Any tickets you\u2019ve already generated can still be edited and pushed to Jira \u2014 no work is lost.',
  },
];

// CheckCircle icon export for pricing feature lists
export { CheckCircleIcon };